// @flow

import type {
  AlgoliaSearchResultList,
  AlgoliaRecord,
  SearchResult,
  SearchResultTypeEnum,
} from 'docs/data';

const tagToType = (tag): SearchResultTypeEnum => {
  switch (tag) {
    case 'js':
      return 'jsref';
    case 'api':
      return 'apiref';
    case 'cli':
      return 'cliref';
    case 'docs':
    default:
      // Algolia has a tag containing 'docs' because our url is stripe.com/docs,
      // and that is how it determines the tag. Since this a singular result,
      // we turn it to 'doc'
      return 'doc';
  }
};

const buildSearchResult = (hit: AlgoliaRecord): SearchResult => {
  const hierarchyKeys = Object.keys(hit.hierarchy)
    .filter((lvl) => lvl !== 'lvl0') // Ignore the lvl0 to avoid "Documentation" and "API" to be a breadcrumb.
    .sort();

  let breadcrumbs = hierarchyKeys
    .map((hierarchyKey) => {
      const algoliaCrumb = hit._highlightResult.hierarchy[hierarchyKey];
      if (!algoliaCrumb) {
        return null;
      }

      const crumb = algoliaCrumb.value;
      const crumbWithoutHighlight = hit.hierarchy[hierarchyKey] || '';
      if (crumbWithoutHighlight.match(/<|>/)) {
        // Algolia will place <strong> tags in the _highlightResult.
        // In the case a value has any <'s or >'s in the actual text
        // (for example, "stripe fixtures <filepath>" is a potential value),
        // do not use Algolia's highlighted version and replace the < and >
        // with entities so the browser does not try to render values like <filepath>
        // as an html tag.
        return crumbWithoutHighlight
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      } else {
        return crumb;
      }
    })
    .filter(Boolean)
    .join(' › ');

  // In some rare cases the Algolia index might be missing breadcrumb information.
  // In these cases, show Home > Doc Title to keep the design consistent.
  if (!breadcrumbs) {
    breadcrumbs = `Home › ${hit.section_title}`;
  }

  const url = new URL(hit.url);
  const urlWithHash = url.pathname + (url.hash === '#content' ? '' : url.hash);
  const type: SearchResultTypeEnum = tagToType(hit.tags[0]);
  const tags: Array<string> = hit.tags || [];
  const highlightedTitle = hit._highlightResult.section_title
    ? hit._highlightResult.section_title.value
    : hit.section_title;
  let content =
    hit._highlightResult.content && hit._highlightResult.content.value;

  if (typeof content === 'string') {
    content = content.trim();
  }

  switch (type) {
    case 'apiref': {
      return {
        title: breadcrumbs,
        url: urlWithHash,
        content,
        tags,
        id: hit.objectID,
        isExternal: true,
        type: 'apiref',
      };
    }
    case 'jsref': {
      const methodSignature = hit.section_title.match(/\(.*\)/)
        ? hit.section_title
        : '';
      return {
        title: breadcrumbs,
        url: urlWithHash,
        content,
        tags,
        id: hit.objectID,
        isExternal: true,
        methodSignature,
        type: 'jsref',
      };
    }
    case 'cliref': {
      // cli ref titles are commands, so add 'stripe' to the beginning to make
      // it look like the full stripe cli command
      const methodSignature = hit.section_title
        ? `stripe ${hit.section_title}`
        : undefined;
      return {
        title: `Stripe CLI › ${breadcrumbs}`,
        url: urlWithHash,
        tags,
        id: hit.objectID,
        isExternal: true,
        methodSignature,
        type: 'cliref',
      };
    }
    default:
    case 'doc': {
      return {
        title: highlightedTitle,
        breadcrumbs,
        url: urlWithHash,
        content,
        tags,
        id: hit.objectID,
        isExternal: false,
        type: 'doc',
      };
    }
  }
};

export const formatSearchResults = (
  results: AlgoliaSearchResultList,
): Array<SearchResult> => {
  return results.hits.map(buildSearchResult).filter((result) => !!result.title);
};
